import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  PlusCircleOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { Empty, Progress, Tag, Tooltip } from 'antd';
import { Skill } from '../../job/models/job.model';
import { formatApplicationSkillScore } from '../utils/application-relevance-labels';
import './JobApplicationSkills.css';

interface JobApplicationSkillsProps {
  skills: Skill[];
  jobSkills?: Skill[];
  score?: number | null;
}

type SkillStatus = 'met' | 'level-gap' | 'missing';

export interface ComparedRoleSkill {
  requirement: Skill;
  candidate?: Skill;
  status: SkillStatus;
}

export interface SkillComparison {
  roleSkills: ComparedRoleSkill[];
  additionalSkills: Skill[];
  roleSkillCount: number;
  foundCount: number;
  metCount: number;
  levelGapCount: number;
  missingCount: number;
  coverage: number | null;
}

const levelRank: Record<string, number> = {
  beginner: 1,
  intermediate: 2,
  advanced: 3,
  expert: 4,
};

function normalizedName(skill: Skill): string {
  return (skill?.skillName || '').trim().toLowerCase();
}

function normalizedLevel(skill?: Skill): string {
  return (skill?.skillLevel || '').trim().toLowerCase();
}

function levelLabel(skill?: Skill): string {
  const level = normalizedLevel(skill);
  return level
    ? `${level.charAt(0).toUpperCase()}${level.slice(1)}`
    : 'Not specified';
}

function uniqueSkills(skills: Skill[]): Skill[] {
  const unique = new Map<string, Skill>();
  skills.forEach((skill) => {
    const key = normalizedName(skill);
    if (!key) return;
    const current = unique.get(key);
    if (
      !current ||
      (levelRank[normalizedLevel(skill)] || 0) >
        (levelRank[normalizedLevel(current)] || 0)
    ) {
      unique.set(key, skill);
    }
  });
  return Array.from(unique.values());
}

export function compareApplicationSkills(
  candidateSkills: Skill[] = [],
  jobSkills: Skill[] = []
): SkillComparison {
  const candidate = uniqueSkills(candidateSkills);
  const requirements = uniqueSkills(jobSkills);
  const candidateByName = new Map(
    candidate.map((skill) => [normalizedName(skill), skill])
  );
  const requirementNames = new Set(
    requirements.map((skill) => normalizedName(skill))
  );

  const roleSkills = requirements.map((requirement): ComparedRoleSkill => {
    const candidateSkill = candidateByName.get(normalizedName(requirement));
    if (!candidateSkill) {
      return { requirement, status: 'missing' };
    }

    const requiredLevel = normalizedLevel(requirement);
    const candidateLevel = normalizedLevel(candidateSkill);
    const meetsLevel =
      !requiredLevel ||
      (Boolean(candidateLevel) &&
        (levelRank[candidateLevel] || 0) >= (levelRank[requiredLevel] || 0));

    return {
      requirement,
      candidate: candidateSkill,
      status: meetsLevel ? 'met' : 'level-gap',
    };
  });

  const foundCount = roleSkills.filter(
    (skill) => skill.status !== 'missing'
  ).length;
  const metCount = roleSkills.filter((skill) => skill.status === 'met').length;
  const levelGapCount = roleSkills.filter(
    (skill) => skill.status === 'level-gap'
  ).length;
  const missingCount = roleSkills.filter(
    (skill) => skill.status === 'missing'
  ).length;
  const additionalSkills = candidate
    .filter((skill) => !requirementNames.has(normalizedName(skill)))
    .sort(
      (first, second) =>
        (levelRank[normalizedLevel(second)] || 0) -
          (levelRank[normalizedLevel(first)] || 0) ||
        first.skillName.localeCompare(second.skillName)
    );

  return {
    roleSkills,
    additionalSkills,
    roleSkillCount: requirements.length,
    foundCount,
    metCount,
    levelGapCount,
    missingCount,
    coverage: requirements.length
      ? Math.round((foundCount / requirements.length) * 100)
      : null,
  };
}

const statusDetails: Record<
  SkillStatus,
  { label: string; icon: React.ReactNode; color: string }
> = {
  met: {
    label: 'Meets target',
    icon: <CheckCircleOutlined />,
    color: 'success',
  },
  'level-gap': {
    label: 'Level gap',
    icon: <ExclamationCircleOutlined />,
    color: 'warning',
  },
  missing: {
    label: 'Not found',
    icon: <CloseCircleOutlined />,
    color: 'error',
  },
};

const JobApplicationSkills: React.FC<JobApplicationSkillsProps> = ({
  skills = [],
  jobSkills = [],
  score,
}) => {
  const comparison = compareApplicationSkills(skills, jobSkills);
  const formattedScore = formatApplicationSkillScore(score);
  const [scoreValue, scoreLabel] = formattedScore.split(' · ');

  return (
    <section
      className="application-skills"
      aria-labelledby="application-skills-heading"
    >
      <div className="application-skills__hero">
        <div className="application-skills__intro">
          <span>Candidate capability map</span>
          <h2 id="application-skills-heading">Skills fit</h2>
          <p>
            Compare the role requirements with the candidate profile, including
            proficiency gaps that a name-only match would miss.
          </p>
        </div>

        <div className="application-skills__scoreboard">
          <div className="application-skills__score">
            <span>Application score</span>
            <strong>{scoreValue || '—'}</strong>
            <small>{scoreLabel || 'Not scored'}</small>
          </div>
          <div className="application-skills__coverage">
            <div>
              <span>Profile coverage</span>
              <strong>
                {comparison.coverage == null ? '—' : `${comparison.coverage}%`}
              </strong>
            </div>
            <Progress
              percent={comparison.coverage ?? 0}
              showInfo={false}
              strokeColor="#1677ff"
              trailColor="rgba(22, 119, 255, 0.12)"
            />
            <small>
              {comparison.roleSkillCount
                ? `${comparison.foundCount} of ${comparison.roleSkillCount} role skills found`
                : 'No role skills configured'}
            </small>
          </div>
        </div>
      </div>

      <div
        className="application-skills__metrics"
        aria-label="Skill comparison summary"
      >
        <div className="application-skill-metric application-skill-metric--met">
          <CheckCircleOutlined aria-hidden />
          <span>Meet target</span>
          <strong>{comparison.metCount}</strong>
        </div>
        <div className="application-skill-metric application-skill-metric--gap">
          <ExclamationCircleOutlined aria-hidden />
          <span>Level gaps</span>
          <strong>{comparison.levelGapCount}</strong>
        </div>
        <div className="application-skill-metric application-skill-metric--missing">
          <CloseCircleOutlined aria-hidden />
          <span>Not found</span>
          <strong>{comparison.missingCount}</strong>
        </div>
        <div className="application-skill-metric application-skill-metric--extra">
          <PlusCircleOutlined aria-hidden />
          <span>Extra strengths</span>
          <strong>{comparison.additionalSkills.length}</strong>
        </div>
      </div>

      <div className="application-skills__content">
        <section
          className="application-skills-card application-skills-card--requirements"
          aria-labelledby="role-skills-heading"
        >
          <div className="application-skills-card__header">
            <div>
              <h3 id="role-skills-heading">Role requirements</h3>
              <p>Presence and proficiency compared skill by skill.</p>
            </div>
            <Tag>{comparison.roleSkillCount} skills</Tag>
          </div>

          {comparison.roleSkills.length ? (
            <div className="application-role-skills">
              {comparison.roleSkills.map((skill) => {
                const details = statusDetails[skill.status];
                return (
                  <article
                    className={`application-role-skill application-role-skill--${skill.status}`}
                    key={normalizedName(skill.requirement)}
                  >
                    <span className="application-role-skill__status-icon">
                      {details.icon}
                    </span>
                    <div className="application-role-skill__identity">
                      <strong>{skill.requirement.skillName}</strong>
                      <span>Role target: {levelLabel(skill.requirement)}</span>
                    </div>
                    <div className="application-role-skill__candidate">
                      <span>Candidate level</span>
                      <strong>
                        {skill.candidate
                          ? levelLabel(skill.candidate)
                          : 'Not listed'}
                      </strong>
                    </div>
                    <Tag color={details.color} icon={details.icon}>
                      {details.label}
                    </Tag>
                  </article>
                );
              })}
            </div>
          ) : (
            <Empty
              className="application-skills__empty"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div>
                  <strong>No role skills configured</strong>
                  <span>
                    Add skills to the job to create a meaningful comparison.
                  </span>
                </div>
              }
            />
          )}
        </section>

        <section
          className="application-skills-card"
          aria-labelledby="additional-skills-heading"
        >
          <div className="application-skills-card__header">
            <div>
              <h3 id="additional-skills-heading">Additional strengths</h3>
              <p>Profile skills that are not explicit role requirements.</p>
            </div>
            <Tag color="blue">{comparison.additionalSkills.length}</Tag>
          </div>

          {comparison.additionalSkills.length ? (
            <div className="application-additional-skills">
              {comparison.additionalSkills.map((skill) => {
                const level = normalizedLevel(skill);
                return (
                  <Tooltip
                    key={normalizedName(skill)}
                    title={`${levelLabel(skill)} proficiency`}
                  >
                    <article className="application-additional-skill">
                      <span
                        className={`application-additional-skill__level application-additional-skill__level--${
                          level || 'unknown'
                        }`}
                        aria-hidden
                      />
                      <div>
                        <strong>{skill.skillName}</strong>
                        <span>{levelLabel(skill)}</span>
                      </div>
                    </article>
                  </Tooltip>
                );
              })}
            </div>
          ) : (
            <Empty
              className="application-skills__empty"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No additional profile skills found."
            />
          )}
        </section>
      </div>

      <div className="application-skills__guidance">
        <SafetyCertificateOutlined aria-hidden />
        <div>
          <strong>How to use this view</strong>
          <p>
            The application score is the stored assessment. Profile coverage is
            a transparent name-and-level comparison; verify critical gaps during
            the interview.
          </p>
        </div>
      </div>
    </section>
  );
};

export default JobApplicationSkills;
